import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isoDate } from "../dateUtils";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus, NotePencil, Trophy, ArrowRight, CalendarBlank, Path } from "@phosphor-icons/react";
import { FABSpeedDial } from "../components/fab";
import { QuickLogSheet } from "../components/quicklog/QuickLogSheet";
import { PullToRefresh } from "../components/PullToRefresh";
import { ResponsiveSwitch } from "../components/ResponsiveSwitch";

import { useDashboard, useBurnoutRisk } from "../dashboardApi";
import { SkeletonCard } from "../components/Skeleton";
import { InlineError } from "../components/InlineError";
import { useWellnessList } from "../wellnessApi";
import { CHECKIN_FIELDS, hasEntryOn, latestMetric } from "../lib/wellnessLatest";
import { hrvDeltaMs } from "../lib/hrvUnits";
import { useInsightsFeed, InsightEvent } from "../insightsApi";
import { useLatestCoachNote } from "../coachNoteApi";
import { useUnitsPreference } from "../lib/useUnitsPreference";
import { useHrvBaseline, usePmcSeries, useZonesDistribution } from "../athleteMetricsApi";
import { useWorkstationPeriodization } from "../hooks/indigo/useWorkstationApi";
import { usePlan, WorkoutItem } from "../planApi";

import { ReadinessSubSignal } from "../components/today/ReadinessRing";
import { TodayHeroCard } from "../components/today/TodayHeroCard";
import { GoalProposalBanner } from "../components/GoalProposalBanner";
import { humanizeReason, reasonBandKey, formatRaceTime } from "../components/today/todayHeroUtils";
import { CoachCard } from "../components/today/CoachCard";
import { MetricDetailDrawer } from "../components/indigo/MetricDetailDrawer";
import { GlanceRail, type GlanceItem } from "../components/today/GlanceRail";
import { RiskChipsStrip } from "../components/today/RiskChipsStrip";
import { EventsFeed, PatternEvent } from "../components/today/EventsFeed";
import { RaceCountdown, RaceForecastChip } from "../components/today/RaceCountdown";
import { AiBrief } from "../components/today/AiBrief";
import { LiveWorkoutChip } from "../components/today/LiveWorkoutChip";
import { TodayDuelWidget } from "../components/today/TodayDuelWidget";
import { TodayProtocolRow } from "../components/today/TodayProtocolRow";
import { TodayEveningBlock } from "../components/today/TodayEveningBlock";
import { TodayYesterdayCard } from "../components/today/TodayYesterdayCard";
import { FeatureTeaser } from "../components/ui/FeatureTeaser";
import { RaceWeekChecklistSection } from "./RaceWeekPage";
import { useMe } from "../auth";
import { isCalledOffStatus, isDoneStatus } from "../workoutStatus";
import { TSB_DEEP_FATIGUE, TSB_CAUTION, PHASE_LOOKAHEAD_DAYS } from "../lib/scienceThresholds";

// INDIGO 9-палитра дисциплин (DESIGN_CANON_MASTER раздел 1.7, ratified 2026-07-04).
// Дублирует --disc-* из index.css для SVG-дотов; правки — синхронно с CSS.
const SPORT_COLOR: Record<string, string> = {
  swim: "var(--disc-swim)",
  cycle: "var(--disc-bike)",
  gravel: "var(--disc-gravel)",
  run: "var(--disc-run)",
  trail: "var(--disc-trail)",
  triathlon: "var(--disc-triathlon)",
  duathlon: "var(--disc-duathlon)",
  hyrox: "var(--disc-hyrox)",
  strength: "var(--disc-strength)",
  other: "var(--fg-muted)",
};

function SportDot({ discipline, size = 5 }: { discipline: string | null; size?: number }) {
  const color = SPORT_COLOR[discipline ?? "other"] ?? "var(--fg-muted)";
  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

function insightToEvent(insight: InsightEvent): PatternEvent {
  return {
    key: insight.key,
    label: insight.label,
    summary: insight.summary,
    severity: insight.severity,
    isNew: insight.is_new,
  };
}

/**
 * W-REBUILD-1 — color_reasons приходят с бэка в тех-формате
 * («🔴 load: 45/100 — lnRMSSD z=-0.99σ»). До атлета доходит только
 * человеческая часть: метка метрики, балл, и либо человеческий хвост,
 * либо фраза-диапазон. Формулы/σ/z — никогда (canon «формулы — в код»).
 */
function reasonsToEvents(
  reasons: string[],
  t: (k: string, opts?: Record<string, unknown>) => string,
): PatternEvent[] {
  return reasons.slice(0, 5).map((reason, i) => {
    const h = humanizeReason(reason);
    const label = h.labelKey ? t(h.labelKey, { defaultValue: h.rawLabel }) : h.rawLabel;
    let summary =
      h.tail ?? t(reasonBandKey(h.score, h.higherIsBetter), { defaultValue: "" });
    // Хвост не должен повторять переведённую метку («Нагрузка · нагрузка…»).
    if (summary.toLowerCase().startsWith(label.toLowerCase())) {
      summary = summary.slice(label.length).replace(/^[\s:—·-]+/, "").trim() || summary;
    }
    return {
      key: `reason-${i}`,
      label,
      summary,
      severity: h.severity,
    };
  });
}

function dashboardGoalToRace(
  goal: ReturnType<typeof useDashboard>["data"] extends infer D
    ? D extends { goal: infer G }
      ? G
      : null
    : null,
  unit: "km" | "mi" = "km",
  t?: (k: string, opts?: Record<string, unknown>) => string,
): null | {
  name: string;
  startDate: string;
  distanceLabel: string;
  targetTime: string | null;
  forecasts: RaceForecastChip[];
} {
  if (!goal) return null;
  if (goal.goal_type !== "race" || !goal.race_date) return null;
  const formatDist = (km: number): string => {
    const v = unit === "mi" ? (km * 0.6213711922).toFixed(2) : km.toString();
    return `${v} ${unit}`;
  };
  // W-REBUILD-1: race_distance — enum-ключ, не показывать сырым
  // (HALF_MARATHON-инцидент). Локализация через dashboard:race_labels.
  const localized = goal.race_distance
    ? t?.(`race_labels.${goal.race_distance}`, { defaultValue: goal.race_distance }) ??
      goal.race_distance
    : null;
  const distanceLabel = localized
    ? `${localized}${goal.custom_distance_km ? ` · ${formatDist(goal.custom_distance_km)}` : ""}`
    : goal.custom_distance_km
    ? formatDist(goal.custom_distance_km)
    : t?.("race.fallback_name", { defaultValue: "Гонка" }) ?? "Гонка";

  const forecasts: RaceForecastChip[] = [];
  const predicted = formatRaceTime(goal.predicted_seconds);
  const target = formatRaceTime(goal.target_time_seconds);
  if (predicted)
    forecasts.push({
      label: t?.("race.forecast_label", { defaultValue: "Прогноз" }) ?? "Прогноз",
      value: predicted,
      tone: "green",
    });
  if (target)
    forecasts.push({
      label: t?.("race.target_label", { defaultValue: "Цель" }) ?? "Цель",
      value: target,
      tone: "accent",
    });

  return { name: distanceLabel, startDate: goal.race_date, distanceLabel, targetTime: target, forecasts };
}

function formatNoteAge(iso: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const diffMin = Math.max(0, Math.floor((Date.now() - then) / 60_000));
  if (diffMin < 1) return t("rel_just_now", { defaultValue: "только что" });
  if (diffMin < 60) return t("rel_mins", { n: diffMin, defaultValue: `${diffMin} мин` });
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return t("rel_hours", { n: diffH, defaultValue: `${diffH} ч` });
  const diffD = Math.floor(diffH / 24);
  return t("rel_days", { n: diffD, word: "дн", defaultValue: `${diffD} дн` });
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Сколько недель доступно свайпом назад и вперёд от текущей. */
export const WEEK_STRIP_BACK = 8;
export const WEEK_STRIP_FORWARD = 4;
/** Больше четырёх точек в ячейку дня не помещается — остаток уходит в «+N». */
const MAX_DOTS = 4;

/**
 * Горизонтальная прокрутка с запасным путём. `Element.scrollTo` есть не
 * везде — его нет в jsdom и не гарантирован в старых webview, — а без
 * запасного пути карусель открывалась бы не на текущей неделе и роняла
 * рендер при монтировании.
 */
function scrollLeftTo(el: HTMLElement, left: number, smooth: boolean) {
  if (typeof el.scrollTo === "function") {
    el.scrollTo({ left, behavior: smooth ? "smooth" : "auto" });
  } else {
    el.scrollLeft = left;
  }
}

/** Понедельник недели, содержащей дату. */
function mondayOf(d: Date): Date {
  const out = new Date(d);
  const dow = out.getDay();
  out.setDate(out.getDate() + (dow === 0 ? -6 : 1 - dow));
  out.setHours(12, 0, 0, 0);
  return out;
}

/**
 * Диапазон, который карусель обязана прогрузить. Живёт снаружи компонента:
 * тот же расчёт нужен странице, чтобы запросить план ровно на эти недели, и
 * разъезд двух копий дал бы пустые ячейки на краях без единой ошибки.
 */
export function weekStripRange(today: Date): { from: string; to: string } {
  const first = mondayOf(today);
  first.setDate(first.getDate() - 7 * WEEK_STRIP_BACK);
  const last = mondayOf(today);
  last.setDate(last.getDate() + 7 * WEEK_STRIP_FORWARD + 6);
  return { from: isoDate(first), to: isoDate(last) };
}

/**
 * Карусель недель для «Сегодня» — свайп по горизонтали, одна неделя за жест.
 *
 * Механика — нативный CSS scroll-snap (как в GoalRoadmapCarousel), без
 * библиотеки и без обработчиков касаний: браузер сам даёт инерцию и
 * доводку, а клавиатура и трекпад работают даром. Ширина страницы равна
 * ширине контейнера, поэтому свайп не может остановиться между неделями.
 *
 * Точка на дне — КАЖДАЯ тренировка этого дня, а не первая: до правки в
 * ячейке рисовался `dayWorkouts[0]`, и день с плаванием, силовой и великом
 * выглядел так же, как день с одной пробежкой.
 */
export function TodayWeekStrip({
  items,
  todayIso,
  onPick,
}: {
  items: WorkoutItem[];
  todayIso: string;
  onPick: (iso: string) => void;
}) {
  const { t, i18n } = useTranslation("dashboard");
  const weekdays = useMemo(
    () => t("weekdays_short", { returnObjects: true }) as string[],
    [t],
  );
  const scroller = useRef<HTMLDivElement | null>(null);
  const [activeWeek, setActiveWeek] = useState(WEEK_STRIP_BACK);

  const weeks = useMemo(() => {
    const base = mondayOf(new Date());
    return Array.from({ length: WEEK_STRIP_BACK + 1 + WEEK_STRIP_FORWARD }, (_, wi) => {
      const monday = new Date(base);
      monday.setDate(base.getDate() + 7 * (wi - WEEK_STRIP_BACK));
      const days = Array.from({ length: 7 }, (_, di) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + di);
        const iso = isoDate(d);
        const dayWorkouts = items.filter((w) => w.scheduled_date === iso);
        return {
          iso,
          dayNum: d.getDate(),
          label: weekdays[d.getDay()] ?? "—",
          isToday: iso === todayIso,
          isPast: iso < todayIso,
          hasDone: dayWorkouts.some((w) => isDoneStatus(w.status)),
          // Точку не получает только отменённая: день, из которого сессию
          // убрали, не должен выглядеть занятым. Перенесённая — получает,
          // она живая и стоит уже в своём новом дне (так же её считает
          // `plan_retrospective`).
          dots: dayWorkouts.filter((w) => !isCalledOffStatus(w.status)),
        };
      });
      return { key: isoDate(monday), monday, days };
    });
  }, [items, todayIso, weekdays]);

  // Открываемся на текущей неделе. `behavior: auto` — прыжок без анимации:
  // видеть, как экран проматывается с восьмой недели назад, незачем.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    scrollLeftTo(el, el.clientWidth * WEEK_STRIP_BACK, false);
  }, []);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el || el.clientWidth === 0) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActiveWeek((prev) => (prev === idx ? prev : idx));
  }, []);

  // Стрелки листают недели. Свайпа на десктопе нет, а тянуть скрытую полосу
  // мышью — не навигация; та же клавиша заодно даёт путь с клавиатуры.
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const el = scroller.current;
    if (!el) return;
    const last = WEEK_STRIP_BACK + WEEK_STRIP_FORWARD;
    const idx = Math.round(el.scrollLeft / (el.clientWidth || 1));
    const next = Math.min(last, Math.max(0, idx + (e.key === "ArrowRight" ? 1 : -1)));
    if (next === idx) return;   // на краю — отдаём клавишу странице
    e.preventDefault();
    scrollLeftTo(el, el.clientWidth * next, true);
  }, []);

  const current = weeks[activeWeek];
  const rangeLabel = useMemo(() => {
    if (!current) return "";
    const end = new Date(current.monday);
    end.setDate(end.getDate() + 6);
    const fmt = (d: Date) =>
      d.toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
    return `${fmt(current.monday)} — ${fmt(end)}`;
  }, [current, i18n.language]);

  return (
    <div data-testid="today-week-carousel">
      {/* Подпись обязательна: без неё свайп дезориентирует — семь чисел без
          месяца одинаково выглядят на любой неделе. */}
      <div className="flex items-baseline justify-between mb-1.5 px-1">
        <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-fg-muted">
          {activeWeek === WEEK_STRIP_BACK
            ? t("week_strip.current", { defaultValue: "Эта неделя" })
            : rangeLabel}
        </span>
        {activeWeek !== WEEK_STRIP_BACK && (
          <button
            type="button"
            data-testid="today-week-back-to-current"
            onClick={() => {
              const el = scroller.current;
              if (el) scrollLeftTo(el, el.clientWidth * WEEK_STRIP_BACK, true);
            }}
            className="font-mono text-[9px] uppercase tracking-[0.16em] text-accent"
          >
            {t("week_strip.back_to_current", { defaultValue: "К текущей" })}
          </button>
        )}
      </div>

      <div
        ref={scroller}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
        data-testid="today-week-strip"
        // Роль и подпись — потому что это уже не просто ряд кнопок, а
        // область с собственной навигацией: без них экранный диктор
        // объявит семь дат и умолчит, что рядом есть другие недели.
        role="group"
        aria-label={t("week_strip.aria", { defaultValue: "Недели, листаются влево и вправо" })}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide overscroll-x-contain"
      >
        {weeks.map((week) => (
          <div key={week.key} className="snap-center shrink-0 w-full flex gap-1.5">
            {week.days.map((day) => (
              <button
                key={day.iso}
                type="button"
                onClick={() => onPick(day.iso)}
                data-testid={`today-week-day-${day.iso}`}
                className={[
                  "flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-[8px] border transition-colors",
                  day.isToday
                    ? "bg-bg-raised border-[rgb(var(--indigo-300-rgb) / 0.28)] text-fg-primary"
                    : "bg-transparent border-[rgba(230,232,255,0.06)] text-fg-secondary hover:bg-bg-surface",
                ].join(" ")}
              >
                <span className="text-[8.5px] uppercase tracking-[0.18em] font-bold">
                  {day.label}
                </span>
                <span
                  className={[
                    "text-[13px] font-semibold tabular-nums",
                    day.isToday ? "text-fg-primary" : day.isPast ? "text-fg-secondary" : "text-fg-muted",
                  ].join(" ")}
                >
                  {day.dayNum}
                </span>
                <div className="h-[9.5px] flex items-center justify-center gap-[2px]">
                  {day.dots.slice(0, MAX_DOTS).map((w) => (
                    <SportDot key={w.id} discipline={w.discipline} size={5} />
                  ))}
                  {day.dots.length > MAX_DOTS && (
                    <span className="text-[7px] font-mono text-fg-muted leading-none">
                      +{day.dots.length - MAX_DOTS}
                    </span>
                  )}
                </div>
                {day.hasDone && (
                  <span className="text-[8px] text-ok font-bold">✓</span>
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * useTodayData — все данные/деривации экрана «Сегодня», отделённые от вёрстки.
 * Общий источник для мобильного (<TodayMobile>) и десктопного (<TodayDesktop>)
 * лейаутов — одна логика, разная композиция (2026-07-25, dedicated desktop).
 * Владеет data-смежным состоянием (openMetric — открытие drawer из glance-rail).
 */
function useTodayData() {
  const { t, i18n } = useTranslation("dashboard");
  const { t: tFab } = useTranslation("common");
  const nav = useNavigate();
  const qc = useQueryClient();
  const [quickLogOpen, setQuickLogOpen] = useState(false);
  const [openMetric, setOpenMetric] = useState<string | null>(null);

  const onPullRefresh = useCallback(async () => {
    await Promise.allSettled([
      qc.invalidateQueries({ queryKey: ["athlete-dashboard"] }),
      qc.invalidateQueries({ queryKey: ["athlete-insights"] }),
      qc.invalidateQueries({ queryKey: ["athlete-coach-note"] }),
      qc.invalidateQueries({ queryKey: ["athlete-wellness"] }),
      qc.invalidateQueries({ queryKey: ["athlete-burnout-risk"] }),
    ]);
  }, [qc]);

  const { data: dashboard, isLoading: dashLoading } = useDashboard();
  const { data: me } = useMe();
  const { data: burnoutRisk } = useBurnoutRisk();
  const { data: wellness } = useWellnessList(14);
  const lang = (i18n?.language ?? "ru").slice(0, 2);
  const { data: insights, isError: insightsError, refetch: refetchInsights } = useInsightsFeed({ lang });
  const { data: coachNote, isError: coachNoteError, refetch: refetchCoachNote } = useLatestCoachNote();
  const { data: pmcSeries } = usePmcSeries(6);
  const { data: zonesDist } = useZonesDistribution(28);
  const { data: hrvBaseline } = useHrvBaseline(14);

  // Current week Mon-Sun for week strip
  const todayDate = new Date();
  const dow = todayDate.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(todayDate);
  monday.setDate(todayDate.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const weekFrom = isoDate(monday);
  const weekTo = isoDate(sunday);
  const { data: weekPlan } = usePlan(weekFrom, weekTo);
  const weekItems = weekPlan?.items ?? [];

  // Отдельный запрос под карусель. Расширять `weekItems` нельзя: на нём
  // висят счётчик «сделано из N» и поиск завтрашней — оба про ТЕКУЩУЮ
  // неделю, и от диапазона в тринадцать недель они бы молча наврали.
  const stripRange = useMemo(() => weekStripRange(new Date()), []);
  const { data: stripPlan } = usePlan(stripRange.from, stripRange.to);
  const stripItems = stripPlan?.items ?? weekItems;

  const subSignals: ReadinessSubSignal[] = useMemo(() => {
    if (!dashboard) return [];
    const out: ReadinessSubSignal[] = [];
    if (dashboard.sleep_score_trend) {
      out.push({
        label: t("readiness.sub_sleep", { defaultValue: "Сон" }),
        tone: dashboard.sleep_score_trend === "down" ? "amber" : "green",
      });
    }
    if (dashboard.hrv_trend) {
      out.push({ label: "HRV", tone: dashboard.hrv_trend === "down" ? "amber" : "green" });
    }
    if (dashboard.rhr_trend) {
      out.push({ label: "RHR", tone: dashboard.rhr_trend === "up" ? "amber" : "green" });
    }
    return out;
  }, [dashboard, t]);

  // Свежие HRV и сон для HERO.
  //
  // Читается С КОНЦА ряда, а не `wellness[0]` — 2026-08-03. Эндпоинт отдаёт
  // дни по возрастанию и добивает пропуски пустыми строками, поэтому нулевой
  // элемент — самый СТАРЫЙ день окна. Экран печатал под кольцом готовности
  // HRV и сон двухнедельной давности как текущие, а «Форма» рядом читала тот
  // же ряд с конца и показывала другое число.
  const latestHrv = latestMetric(wellness, "hrv").value;
  const latestSleep = latestMetric(wellness, "sleep_hours").value;
  // W-IA-2d — чек-ин сделан сегодня, если за сегодня есть ЗАПОЛНЕННАЯ запись.
  // Сравнение дат тут было вдвойне неверным: брало старейший день окна, а
  // строка за сегодня существует всегда — пустая, пока человек не отметился.
  const checkedInToday = hasEntryOn(wellness, todayISO(), CHECKIN_FIELDS);

  // W-IA-2 раздел 2.1 — позиция в мезоцикле для шапки («BUILD · до смены фазы 4д»).
  const { data: periodization } = useWorkstationPeriodization();
  const phaseLine = useMemo(() => {
    const phases = periodization?.phases ?? [];
    if (!phases.length) return null;
    const iso = todayISO();
    const cur = phases.find((p) => p.start_date <= iso && iso <= p.end_date);
    if (!cur) return null;
    const next = phases.find((p) => p.start_date > iso);
    let tail = "";
    if (next) {
      const days = Math.round(
        (Date.parse(next.start_date) - Date.parse(iso)) / 86_400_000,
      );
      if (days > 0 && days <= PHASE_LOOKAHEAD_DAYS) {
        tail = ` · ${t("phase.next_in", { defaultValue: "смена фазы через {{n}}д", n: days })}`;
      }
    }
    return `${cur.name}${tail}`;
  }, [periodization, t]);
  /**
   * Отклонение HRV от базы — В ОДНИХ ЕДИНИЦАХ (2026-08-03).
   *
   * `/athlete/metrics/hrv` отдаёт lnRMSSD: и ряд, и базу. Логарифм — рабочая
   * форма для статистики (распределение симметричнее, z-оценка честнее), но
   * не для показа. `wellness.hrv` при этом хранится в МИЛЛИСЕКУНДАХ.
   *
   * Здесь стояло `latestHrv - baseline`, то есть из миллисекунд вычитался
   * логарифм: при HRV 67 мс и базе 4.049 экран печатал «+63» вместо «+10».
   * Ошибка не бросалась в глаза, потому что база — маленькое число, и
   * разность выходила похожей на само значение: «HRV 67, +63» читается как
   * «почти весь HRV — превышение», что абсурдно, но правдоподобно с виду.
   *
   * «Форма» ту же базу показывает верно — она делает exp() (см. FormPage,
   * подпись «база 57»). Две страницы, один эндпоинт, разные единицы.
   */
  const hrvDelta = useMemo(
    () => hrvDeltaMs(latestHrv, hrvBaseline?.baseline),
    [latestHrv, hrvBaseline],
  );

  const events: PatternEvent[] = useMemo(() => {
    if (insights?.events && insights.events.length > 0) {
      return insights.events.map(insightToEvent);
    }
    return reasonsToEvents(dashboard?.color_reasons ?? [], t);
  }, [insights, dashboard, t]);

  // W-IA-2c — рельса метрик (glance-rail). Данные из уже существующих
  // хуков страницы; drill-keys совпадают с MetricDetailDrawer contract.
  const glanceItems: GlanceItem[] = useMemo(() => {
    const items: GlanceItem[] = [];
    const lastOf = (arr?: number[] | null) =>
      arr && arr.length ? arr[arr.length - 1] : null;
    const ctl = lastOf(pmcSeries?.ctl) ?? dashboard?.ctl_current ?? null;
    const atl = lastOf(pmcSeries?.atl);
    const fmtDelta = (d: number | null | undefined) =>
      d == null ? undefined : `${d > 0 ? "+" : ""}${Math.round(d)} ${t("glance.delta_window", { defaultValue: "/ 7д" })}`;
    const deltaTone = (d: number | null | undefined, downIsBad: boolean) => {
      if (d == null || d === 0) return "muted" as const;
      const bad = downIsBad ? d < 0 : d > 0;
      return bad ? ("warn" as const) : ("good" as const);
    };
    if (ctl != null) {
      items.push({
        key: "ctl", label: "CTL", value: String(Math.round(ctl)),
        sub: fmtDelta(pmcSeries?.delta_ctl_7d),
        subTone: deltaTone(pmcSeries?.delta_ctl_7d, true),
        onClick: () => setOpenMetric("ctl"),
      });
    }
    if (atl != null) {
      items.push({
        key: "atl", label: "ATL", value: String(Math.round(atl)),
        sub: fmtDelta(pmcSeries?.delta_atl_7d),
        subTone: deltaTone(pmcSeries?.delta_atl_7d, false),
        onClick: () => setOpenMetric("atl"),
      });
    }
    if (ctl != null && atl != null) {
      const tsb = Math.round(ctl - atl);
      items.push({
        key: "tsb", label: "TSB", value: `${tsb > 0 ? "+" : ""}${tsb}`,
        sub: t("glance.tsb_sub", { defaultValue: "баланс" }),
        subTone: tsb < TSB_DEEP_FATIGUE ? "bad" : tsb < TSB_CAUTION ? "warn" : "muted",
        onClick: () => setOpenMetric("tsb"),
      });
    }
    if (latestHrv != null) {
      items.push({
        key: "hrv", label: "HRV", value: `${Math.round(latestHrv)}`,
        sub: hrvDelta != null
          ? `${hrvDelta > 0 ? "+" : ""}${hrvDelta} ${t("glance.vs_base", { defaultValue: "к базе" })}`
          : undefined,
        subTone: hrvDelta != null ? (hrvDelta < 0 ? "warn" : "good") : "muted",
        onClick: () => setOpenMetric("hrv_baseline"),
      });
    }
    const percents = zonesDist?.percents ?? [];
    if (percents.length >= 2) {
      const easy = Math.round((percents[0] ?? 0) + (percents[1] ?? 0));
      items.push({
        key: "zones", label: t("glance.zones", { defaultValue: "Зоны 28д" }),
        value: `${easy}%`,
        sub: "Z1–2",
        subTone: "muted",
        onClick: () => nav("/analytics"),
      });
    }
    if (dashboard?.vo2max != null) {
      items.push({
        key: "vo2max", label: "VO₂max", value: dashboard.vo2max.toFixed(1),
        sub: dashboard.vo2max_trend === "up" ? "↗" : dashboard.vo2max_trend === "down" ? "↘" : "→",
        subTone: dashboard.vo2max_trend === "down" ? "warn" : "muted",
        onClick: () => nav("/analytics"),
      });
    }
    const latestRhr = latestMetric(wellness, "rhr").value;
    if (latestRhr != null) {
      items.push({
        key: "rhr", label: "RHR", value: String(Math.round(latestRhr)),
        sub: t("glance.bpm", { defaultValue: "уд/мин" }),
        subTone: "muted",
        onClick: () => nav("/wellness"),
      });
    }
    const latestWeight = latestMetric(wellness, "body_weight").value;
    if (latestWeight != null) {
      items.push({
        key: "weight", label: t("glance.weight", { defaultValue: "Вес" }),
        value: `${latestWeight.toFixed(1)}`,
        sub: t("glance.kg", { defaultValue: "кг" }),
        subTone: "muted",
        onClick: () => nav("/wellness"),
      });
    }
    const sleepVals = (wellness ?? [])
      .slice(0, 7)
      .map((w) => w.sleep_hours)
      .filter((v): v is number => v != null);
    if (sleepVals.length >= 3) {
      const avg = sleepVals.reduce((a, b) => a + b, 0) / sleepVals.length;
      items.push({
        key: "sleep", label: t("glance.sleep", { defaultValue: "Сон 7д" }),
        value: `${avg.toFixed(1)}${t("common:hour_short", { defaultValue: "ч" })}`,
        sub: t("glance.avg", { defaultValue: "среднее" }),
        subTone: "muted",
        onClick: () => nav("/wellness"),
      });
    }
    return items;
  }, [pmcSeries, dashboard?.ctl_current, dashboard?.vo2max, dashboard?.vo2max_trend, latestHrv, hrvDelta, zonesDist, wellness, t, nav]);

  // W-IA-2f — завтрашняя тренировка из недельного плана + текущий TSB.
  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return isoDate(d); })();
  const tomorrowWorkout = weekItems.find((w) => w.scheduled_date === tomorrowISO) ?? null;
  const tsbNow = (() => {
    const last = (arr?: number[] | null) => (arr && arr.length ? arr[arr.length - 1] : null);
    const c = last(pmcSeries?.ctl); const a = last(pmcSeries?.atl);
    return c != null && a != null ? Math.round(c - a) : null;
  })();

  const { unit } = useUnitsPreference();
  const race = useMemo(() => dashboardGoalToRace(dashboard?.goal ?? null, unit, t), [dashboard, unit, t]);

  // W-FIX-C — умное позиционирование гонки: близость старта = высота блока.
  // ≤7 дней — сразу после протокола дня, 8–21 — после недели, дальше — низ.
  const daysToRace = (() => {
    if (!race?.startDate) return null;
    const ms = new Date(race.startDate + "T00:00:00").getTime() - Date.now();
    return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 86_400_000));
  })();
  const raceCountdownBlock = race ? (
    <RaceCountdown
      name={race.name}
      startDate={race.startDate}
      todayDate={todayISO()}
      distanceLabel={race.distanceLabel}
      forecasts={race.forecasts}
      onOpen={() => nav("/race-week")}
    />
  ) : null;

  // W-REBUILD-1 — микро-шапка: локализованная дата вместо сырого верди
  // (раньше в H1 стоял color_reasons[0] с формулами — убито).
  const dateLine = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(i18n?.language ?? "ru", {
        weekday: "short",
        day: "numeric",
        month: "long",
      }).format(new Date());
    } catch {
      // silent: незнакомая локаль в i18n — ISO-дата читаемее падения.
      return todayISO();
    }
  }, [i18n?.language]);

  return {
    onPullRefresh, dashboard, dashLoading, me, burnoutRisk, wellness,
    subSignals, latestHrv, latestSleep, hrvDelta, checkedInToday, phaseLine,
    events, glanceItems, weekItems, stripItems, race, daysToRace, raceCountdownBlock,
    tomorrowWorkout, tsbNow, dateLine, coachNote, coachNoteError,
    refetchCoachNote, insightsError, refetchInsights, nav, t, tFab,
    quickLogOpen, setQuickLogOpen, openMetric, setOpenMetric,
  };
}

function TodayMobile() {
  const {
    onPullRefresh, dashboard, dashLoading, me, burnoutRisk, wellness,
    subSignals, latestHrv, latestSleep, hrvDelta, checkedInToday, phaseLine,
    events, glanceItems, weekItems, stripItems, race, daysToRace, raceCountdownBlock,
    tomorrowWorkout, tsbNow, dateLine, coachNote, coachNoteError,
    refetchCoachNote, insightsError, refetchInsights, nav, t, tFab,
    quickLogOpen, setQuickLogOpen, openMetric, setOpenMetric,
  } = useTodayData();

  if (dashLoading) {
    return (
      <div
        data-testid="today-page-loading"
        aria-live="polite"
        aria-busy="true"
        className="flex flex-col gap-4 px-3 py-4 max-w-[1480px] mx-auto"
      >
        <SkeletonCard className="h-8 w-2/3" />
        <SkeletonCard className="h-32" />
        <SkeletonCard className="h-48" />
        <SkeletonCard className="h-16" />
      </div>
    );
  }

  const today = dashboard?.today ?? null;

  const isRaceToday =
    dashboard?.goal?.goal_type === "race" &&
    !!dashboard?.goal?.race_date &&
    dashboard.goal.race_date === todayISO();
  if (isRaceToday) {
    return (
      <div data-testid="today-page-race-day" className="px-3 py-4 max-w-[1480px] mx-auto">
        <RaceWeekChecklistSection showHero={true} />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={onPullRefresh} testId="today-pull-to-refresh">
    <div data-testid="today-page" className="flex flex-col gap-4 px-3 py-4 max-w-[1480px] mx-auto">

      {/* ── 1. МИКРО-ШАПКА: дата + фаза (W-REBUILD-1). Большой H1 с сырым
             color_reasons[0] убит — вердикт живёт в hero человеческим
             языком. ── */}
      <div data-testid="today-header" className="flex items-baseline gap-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {t("today_label", { defaultValue: "Сегодня" })}
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-fg-secondary font-bold">
          {dateLine}
        </span>
        {phaseLine && (
          <span
            data-testid="today-phase-line"
            className="text-[10px] uppercase tracking-[0.18em] text-fg-muted font-bold ml-auto"
          >
            {phaseLine}
          </span>
        )}
      </div>

      {/* ── 2. ACTIVE WORKOUT CHIP ── */}
      <LiveWorkoutChip />

      {/* ── 2b. ПРЕДЛОЖЕНИЕ ТРЕНЕРА (coach goal proposal) — приоритетнее hero,
             после live-чипа. Silent default: рендерит null без pending-предложений.
             useGoalInbox → GET /athlete/goal/inbox. ── */}
      <GoalProposalBanner />

      {/* ── 3. HERO «кабина решения»: вердикт (burnout-тренд внутри) +
             готовность + тренировка + протокол — один объект. ── */}
      <TodayHeroCard
        readinessScore={dashboard?.readiness_score ?? null}
        tsb={tsbNow}
        subSignals={subSignals}
        burnout={burnoutRisk ?? null}
        hrv={latestHrv}
        hrvDelta={hrvDelta}
        sleepHours={latestSleep}
        checkedInToday={checkedInToday}
        workout={today}
        onStart={today ? () => nav(`/workout/${today.id}/live`) : undefined}
        onOpenWorkout={today ? () => nav(`/workout/${today.id}`) : undefined}
      >
        <TodayProtocolRow
          workout={today ?? null}
          bodyWeightKg={latestMetric(wellness, "body_weight").value}
          embedded
        />
      </TodayHeroCard>

      {/* Risk-чипы workstation — safety-поверхность, остаётся отдельной
          тихой строкой (пусто → не рендерится). */}
      <RiskChipsStrip />

      {/* ── 4. ГОНКА: одна позиция. T−7 — полная карта вызова;
             дальше — компактная строка. ── */}
      {race && daysToRace != null && daysToRace <= 7 && raceCountdownBlock}
      {race && (daysToRace == null || daysToRace > 7) && (
        <button
          type="button"
          data-testid="today-race-line"
          onClick={() => nav("/race-week")}
          className="indigo-card px-4 py-3 flex items-center gap-2.5 text-left hover:bg-bg-raised active:bg-bg-raised transition-colors"
        >
          <span className="w-[3px] h-3.5 rounded-sm bg-ok inline-block shrink-0" />
          <span className="flex-1 min-w-0 text-[13px] text-fg-primary font-semibold truncate">
            {race.name}
          </span>
          <span className="text-[12px] text-fg-secondary tabular-nums shrink-0">
            {daysToRace != null &&
              t("race.line_days", { defaultValue: "через {{n}} дн", n: daysToRace })}
            {race.targetTime &&
              ` · ${t("race.target_label", { defaultValue: "Цель" }).toLowerCase()} ${race.targetTime}`}
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-fg-muted shrink-0" />
        </button>
      )}

      {/* ── 5. ПОЛОСА НЕДЕЛИ — ближний горизонт сразу за сегодняшней
             тренировкой (ARCHITECTURE раздел 1: блок 5 до «требует внимания»).
             Дальше неё — только входящие и вторичное. ── */}
      <div data-testid="today-week-section">
        <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-fg-muted mb-2.5">
          {t("week_strip_label", { defaultValue: "Неделя" })}
        </div>
        <TodayWeekStrip
          items={stripItems}
          todayIso={todayISO()}
          onPick={(iso) => nav(`/day/${iso}`)}
        />
        {/* W-REBUILD-1: CTL из футера недели убран — один дом метрики
            (glance-rail ниже). «0 из 0» по-прежнему не показываем. */}
        {weekItems.length > 0 && (
          <div className="mt-2 px-1">
            <span className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-fg-muted">
              {t("week_done_of", {
                defaultValue: "{{done}} из {{total}}",
                done: weekItems.filter((w) => isDoneStatus(w.status)).length,
                // Отменённые тренер сам снял — они не «невыполненные».
                // «3 из 7» при двух отменах читается как провал недели.
                total: weekItems.filter((w) => !isCalledOffStatus(w.status)).length,
              })}
            </span>
          </div>
        )}
      </div>

      {/* ── 6. ТРЕНЕР — только у Coached-атлета (у Solo блок не существует,
             мандат-развилка 2026-07-04). Заметки нет → компактный empty. ── */}
      {me?.has_coach &&
        (coachNoteError ? (
          <InlineError
            testId="coach-note-error"
            title={t("coach_note.error_title", { defaultValue: "Не удалось загрузить заметку тренера" })}
            message={t("coach_note.error_msg", { defaultValue: "Последняя заметка временно недоступна — попробуем ещё раз." })}
            retryLabel={t("retry", { defaultValue: "Повторить" })}
            onRetry={() => refetchCoachNote()}
          />
        ) : (
          <CoachCard
            coachName={coachNote?.coach_name ?? me?.coach?.name ?? null}
            note={coachNote?.content ?? null}
            relativeTime={coachNote ? formatNoteAge(coachNote.created_at, t) : null}
          />
        ))}

      {/* ── 7. «Метод заметил» ── */}
      <div data-testid="today-method-feed" className="space-y-2">
        <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-fg-muted">
          {t("method_feed_label", { defaultValue: "Метод заметил" })}
        </div>
        <AiBrief />
        {insightsError && events.length === 0 ? (
          <InlineError
            testId="insights-error"
            title={t("insights.error_title", { defaultValue: "Не удалось загрузить ленту инсайтов" })}
            message={t("insights.error_msg", { defaultValue: "События появятся после повторной загрузки." })}
            retryLabel={t("retry", { defaultValue: "Повторить" })}
            onRetry={() => refetchInsights()}
          />
        ) : (
          <EventsFeed events={events} />
        )}
      </div>

      {/* ── BELOW FOLD — secondary widgets ── */}

      {/* W-IA-2h — виджет активной дуэли (кольцо 2, ежедневная мотивация).
          Активная дуэль = атлет сам вступил (self-opt-in) — без неё виджета
          нет, тизер на Сегодня не ставим («Серьёзный спорт. Без шума»). */}
      <TodayDuelWidget />

      {/* Yesterday's workout */}
      <TodayYesterdayCard wo={dashboard?.yesterday ?? null} />

      {/* Старт не выбран → тизер (единственная race-позиция без цели) */}
      {!race && (
        <FeatureTeaser
          testId="today-race-teaser"
          title={t("race.no_goal", { defaultValue: "Главный старт не выбран" })}
          hint={t("race.no_goal_hint", { defaultValue: "Выбери гонку — появится обратный отсчёт и прогноз" })}
          cta={t("race.no_goal_cta", { defaultValue: "К стартам" })}
          to="/events"
        />
      )}

      {/* W-IA-2f — вечерний блок: превью завтра + цель сна */}
      <TodayEveningBlock hour={new Date().getHours()} tomorrow={tomorrowWorkout} tsb={tsbNow} />

      {/* W-REBUILD-1: LifestyleRow убран — плитки «нет данных» / «скоро»
          не заслуживают главного экрана; питание живёт в протоколе hero. */}

      {/* W-IA-2c (IA_CANON_v2 раздел 2.6) — рельса метрик вместо трёх mini-карт.
          v4-правило: число ink-1, Δ/статус — строкой под числом в семантике.
          WeekCalendar (дубль Плана), TodayMorningCard (дубль hero) и
          RecoveryTimer-блок удалены — IA_CANON_v2 раздел 2 «Убрано с Сегодня». */}
      <GlanceRail items={glanceItems} />

      {/* Month recap CTA */}
      <Link
        to="/recap/month"
        className="indigo-card p-4 flex items-center gap-3 hover:bg-bg-raised active:bg-bg-raised transition-colors group"
        data-testid="today-month-recap-cta"
      >
        <div className="w-9 h-9 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center shrink-0">
          <CalendarBlank className="w-4 h-4 text-fg-secondary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-fg-primary">
            {t("month_recap.cta_title", { defaultValue: "Итоги месяца" })}
          </div>
          <div className="text-[11px] text-fg-secondary mt-0.5">
            {t("month_recap.cta_hint", { defaultValue: "Километраж, CTL, гонки и личные рекорды" })}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-fg-muted shrink-0 group-hover:text-fg-primary transition-colors" />
      </Link>

      <QuickLogSheet open={quickLogOpen} onClose={() => setQuickLogOpen(false)} />

      <FABSpeedDial
        icon={Plus}
        label={tFab("fab.today.label")}
        actions={[
          {
            icon: Plus,
            label: tFab("fab.today.primary"),
            hint: tFab("fab.today.primary_hint"),
            onClick: () => setQuickLogOpen(true),
            testId: "fab-today-primary",
          },
          {
            icon: Path,
            label: tFab("fab.today.record", { defaultValue: "Записать" }),
            hint: tFab("fab.today.record_hint", { defaultValue: "GPS-запись тренировки" }),
            onClick: () => nav("/record"),
            testId: "fab-today-record",
          },
          {
            icon: NotePencil,
            label: tFab("fab.today.diary"),
            hint: tFab("fab.today.diary_hint"),
            onClick: () => nav("/diary"),
            testId: "fab-today-diary",
          },
          {
            icon: Trophy,
            label: tFab("fab.today.events"),
            hint: tFab("fab.today.events_hint"),
            onClick: () => nav("/events"),
            testId: "fab-today-events",
          },
        ]}
        testId="fab-today"
      />
      <MetricDetailDrawer
        metricName={openMetric}
        isOpen={openMetric !== null}
        onClose={() => setOpenMetric(null)}
      />
    </div>
    </PullToRefresh>
  );
}

/**
 * TodayDesktop — десктоп-композиция «Сегодня»: тот же контент/компоненты и
 * тот же useTodayData(), но в двухколоночной сетке внутри max-w — вместо
 * растянутой одноколонки. Первичная колонка = кабина решения (hero/гонка/
 * тренер/лента/неделя), вторичная = метрики и вечерний контекст. Honest
 * reflow существующей системы, без выдуманного визуала (2026-07-25).
 */
function TodayDesktop() {
  const {
    onPullRefresh, dashboard, dashLoading, me, burnoutRisk, wellness,
    subSignals, latestHrv, latestSleep, hrvDelta, checkedInToday, phaseLine,
    events, glanceItems, weekItems, stripItems, race, daysToRace, raceCountdownBlock,
    tomorrowWorkout, tsbNow, dateLine, coachNote, coachNoteError,
    refetchCoachNote, insightsError, refetchInsights, nav, t, tFab,
    quickLogOpen, setQuickLogOpen, openMetric, setOpenMetric,
  } = useTodayData();

  if (dashLoading) {
    return (
      <div
        data-testid="today-page-loading"
        aria-live="polite"
        aria-busy="true"
        className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-6 px-6 py-6 max-w-[1480px] mx-auto"
      >
        <div className="flex flex-col gap-4">
          <SkeletonCard className="h-8 w-2/3" />
          <SkeletonCard className="h-48" />
          <SkeletonCard className="h-32" />
        </div>
        <div className="flex flex-col gap-4">
          <SkeletonCard className="h-32" />
          <SkeletonCard className="h-16" />
        </div>
      </div>
    );
  }

  const today = dashboard?.today ?? null;
  const isRaceToday =
    dashboard?.goal?.goal_type === "race" &&
    !!dashboard?.goal?.race_date &&
    dashboard.goal.race_date === todayISO();
  if (isRaceToday) {
    return (
      <div data-testid="today-page-race-day" className="px-6 py-6 max-w-[1480px] mx-auto">
        <RaceWeekChecklistSection showHero={true} />
      </div>
    );
  }

  const raceLine =
    race && (daysToRace == null || daysToRace > 7) ? (
      <button
        type="button"
        data-testid="today-race-line"
        onClick={() => nav("/race-week")}
        className="indigo-card px-4 py-3 flex items-center gap-2.5 text-left hover:bg-bg-raised active:bg-bg-raised transition-colors w-full"
      >
        <span className="w-[3px] h-3.5 rounded-sm bg-ok inline-block shrink-0" />
        <span className="flex-1 min-w-0 text-[13px] text-fg-primary font-semibold truncate">
          {race.name}
        </span>
        <span className="text-[12px] text-fg-secondary tabular-nums shrink-0">
          {daysToRace != null &&
            t("race.line_days", { defaultValue: "через {{n}} дн", n: daysToRace })}
          {race.targetTime &&
            ` · ${t("race.target_label", { defaultValue: "Цель" }).toLowerCase()} ${race.targetTime}`}
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-fg-muted shrink-0" />
      </button>
    ) : null;

  return (
    <PullToRefresh onRefresh={onPullRefresh} testId="today-pull-to-refresh">
      <div data-testid="today-page" className="px-6 py-6 max-w-[1600px] mx-auto">
        <div data-testid="today-header" className="flex items-baseline gap-2 mb-4">
          <span className="text-[10px] uppercase tracking-[0.22em] text-fg-muted font-bold">
            {t("today_label", { defaultValue: "Сегодня" })}
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-fg-secondary font-bold">
            {dateLine}
          </span>
          {phaseLine && (
            <span
              data-testid="today-phase-line"
              className="text-[10px] uppercase tracking-[0.18em] text-fg-muted font-bold ml-auto"
            >
              {phaseLine}
            </span>
          )}
        </div>

        <LiveWorkoutChip />
        <div className="mt-4">
          <GoalProposalBanner />
        </div>

        {/* Метрики-полоса — командный центр: ключевые числа всегда на виду
            сверху (выше «вместительность»), не спрятаны в скролл (PC v1). */}
        <div className="mt-4">
          <GlanceRail items={glanceItems} />
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-6 items-start">
          {/* ── Первичная колонка — кабина решения ── */}
          <div className="flex flex-col gap-4 min-w-0">
            <TodayHeroCard
              readinessScore={dashboard?.readiness_score ?? null}
              tsb={tsbNow}
              subSignals={subSignals}
              burnout={burnoutRisk ?? null}
              hrv={latestHrv}
              hrvDelta={hrvDelta}
              sleepHours={latestSleep}
              checkedInToday={checkedInToday}
              workout={today}
              onStart={today ? () => nav(`/workout/${today.id}/live`) : undefined}
              onOpenWorkout={today ? () => nav(`/workout/${today.id}`) : undefined}
            >
              <TodayProtocolRow
                workout={today ?? null}
                bodyWeightKg={latestMetric(wellness, "body_weight").value}
                embedded
              />
            </TodayHeroCard>

            <RiskChipsStrip />

            {race && daysToRace != null && daysToRace <= 7 && raceCountdownBlock}
            {raceLine}

            {me?.has_coach &&
              (coachNoteError ? (
                <InlineError
                  testId="coach-note-error"
                  title={t("coach_note.error_title", { defaultValue: "Не удалось загрузить заметку тренера" })}
                  message={t("coach_note.error_msg", { defaultValue: "Последняя заметка временно недоступна — попробуем ещё раз." })}
                  retryLabel={t("retry", { defaultValue: "Повторить" })}
                  onRetry={() => refetchCoachNote()}
                />
              ) : (
                <CoachCard
                  coachName={coachNote?.coach_name ?? me?.coach?.name ?? null}
                  note={coachNote?.content ?? null}
                  relativeTime={coachNote ? formatNoteAge(coachNote.created_at, t) : null}
                />
              ))}

            <div data-testid="today-method-feed" className="space-y-2">
              <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-fg-muted">
                {t("method_feed_label", { defaultValue: "Метод заметил" })}
              </div>
              <AiBrief />
              {insightsError && events.length === 0 ? (
                <InlineError
                  testId="insights-error"
                  title={t("insights.error_title", { defaultValue: "Не удалось загрузить ленту инсайтов" })}
                  message={t("insights.error_msg", { defaultValue: "События появятся после повторной загрузки." })}
                  retryLabel={t("retry", { defaultValue: "Повторить" })}
                  onRetry={() => refetchInsights()}
                />
              ) : (
                <EventsFeed events={events} />
              )}
            </div>

            <div data-testid="today-week-section">
              <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-fg-muted mb-2.5">
                {t("week_strip_label", { defaultValue: "Неделя" })}
              </div>
              <TodayWeekStrip
                items={stripItems}
                todayIso={todayISO()}
                onPick={(iso) => nav(`/day/${iso}`)}
              />
              {weekItems.length > 0 && (
                <div className="mt-2 px-1">
                  <span className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-fg-muted">
                    {t("week_done_of", {
                      defaultValue: "{{done}} из {{total}}",
                      done: weekItems.filter((w) => isDoneStatus(w.status)).length,
                      // Отменённые тренер сам снял — они не «невыполненные».
                      // «3 из 7» при двух отменах читается как провал недели.
                      total: weekItems.filter((w) => !isCalledOffStatus(w.status)).length,
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Вторичная колонка — вечерний контекст + мотивация ── */}
          <div className="flex flex-col gap-4 min-w-0">
            <TodayEveningBlock hour={new Date().getHours()} tomorrow={tomorrowWorkout} tsb={tsbNow} />
            <TodayYesterdayCard wo={dashboard?.yesterday ?? null} />
            <TodayDuelWidget />
            {!race && (
              <FeatureTeaser
                testId="today-race-teaser"
                title={t("race.no_goal", { defaultValue: "Главный старт не выбран" })}
                hint={t("race.no_goal_hint", { defaultValue: "Выбери гонку — появится обратный отсчёт и прогноз" })}
                cta={t("race.no_goal_cta", { defaultValue: "К стартам" })}
                to="/events"
              />
            )}
            <Link
              to="/recap/month"
              className="indigo-card p-4 flex items-center gap-3 hover:bg-bg-raised active:bg-bg-raised transition-colors group"
              data-testid="today-month-recap-cta"
            >
              <div className="w-9 h-9 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center shrink-0">
                <CalendarBlank className="w-4 h-4 text-fg-secondary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-fg-primary">
                  {t("month_recap.cta_title", { defaultValue: "Итоги месяца" })}
                </div>
                <div className="text-[11px] text-fg-secondary mt-0.5">
                  {t("month_recap.cta_hint", { defaultValue: "Километраж, CTL, гонки и личные рекорды" })}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-fg-muted shrink-0 group-hover:text-fg-primary transition-colors" />
            </Link>
          </div>
        </div>

        <QuickLogSheet open={quickLogOpen} onClose={() => setQuickLogOpen(false)} />
        <FABSpeedDial
          icon={Plus}
          label={tFab("fab.today.label")}
          actions={[
            {
              icon: Plus,
              label: tFab("fab.today.primary"),
              hint: tFab("fab.today.primary_hint"),
              onClick: () => setQuickLogOpen(true),
              testId: "fab-today-primary",
            },
            {
              icon: Path,
              label: tFab("fab.today.record", { defaultValue: "Записать" }),
              hint: tFab("fab.today.record_hint", { defaultValue: "GPS-запись тренировки" }),
              onClick: () => nav("/record"),
              testId: "fab-today-record",
            },
            {
              icon: NotePencil,
              label: tFab("fab.today.diary"),
              hint: tFab("fab.today.diary_hint"),
              onClick: () => nav("/diary"),
              testId: "fab-today-diary",
            },
            {
              icon: Trophy,
              label: tFab("fab.today.events"),
              hint: tFab("fab.today.events_hint"),
              onClick: () => nav("/events"),
              testId: "fab-today-events",
            },
          ]}
          testId="fab-today"
        />
        <MetricDetailDrawer
          metricName={openMetric}
          isOpen={openMetric !== null}
          onClose={() => setOpenMetric(null)}
        />
      </div>
    </PullToRefresh>
  );
}

export function TodayPage() {
  return <ResponsiveSwitch mobile={<TodayMobile />} desktop={<TodayDesktop />} />;
}
